import { createFileRoute } from "@tanstack/react-router";
import sporty from "@/assets/goblin-option-sporty.png";
import ref from "@/assets/goblin-option-ref.png";
import streetwear from "@/assets/goblin-option-streetwear.png";
import varsity from "@/assets/goblin-option-varsity.png";
import conceptCute from "@/assets/mascot-concept-1-cute.png";
import conceptEdgy from "@/assets/mascot-concept-2-edgy.png";
import conceptSporty from "@/assets/mascot-concept-3-sporty.png";
import heroRefined from "@/assets/mascot-hero-refined.png";
import refHero from "@/assets/mascot-ref-hero.png";
import refLogo from "@/assets/mascot-ref-logo.png";
import refOg from "@/assets/mascot-ref-og.png";
import refOgWhite from "@/assets/mascot-ref-og-white.png";

// Hostnames where this internal preview page should be hidden (return 404).
// Anything else (lovable.app preview, localhost) renders normally.
const PROD_HOSTS = new Set(["bracketgoblin.com", "www.bracketgoblin.com"]);

function isProdHost() {
  if (typeof window === "undefined") return false;
  return PROD_HOSTS.has(window.location.hostname);
}

export const Route = createFileRoute("/mascot-options")({
  head: () => ({
    meta: [
      { title: "Mascot Options — Bracket Goblin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MascotOptionsRouteGate,
});

function MascotOptionsRouteGate() {
  if (isProdHost()) {
    return (
      <div className="min-h-screen bg-background px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-bold text-foreground">404 — Not Found</h1>
        <p className="mt-2 text-sm text-muted-foreground">This page doesn't exist.</p>
      </div>
    );
  }
  return <MascotOptionsPage />;
}

const STYLED_OPTIONS = [
  { src: sporty, name: "Sporty (soccer kit)", note: "Action pose, jersey + cleats, holding a bracket. Most on-brand for FIFA WC." },
  { src: ref, name: "Ref", note: "Chunky cartoon ref with whistle and thumbs up. Friendliest." },
  { src: streetwear, name: "Streetwear / esports", note: "Snapback, basketball jersey, giant pencil. Sly modern logo vibe." },
  { src: varsity, name: "Varsity", note: "Letterman jacket, arms crossed, confident smirk. Classic mascot." },
];

const EARLIER_CONCEPTS = [
  { src: conceptCute, name: "Concept 1 — Cute", note: "Softer, rounder, friendliest read." },
  { src: conceptEdgy, name: "Concept 2 — Edgy", note: "Sharper features, closer to the current/demonic feedback." },
  { src: conceptSporty, name: "Concept 3 — Sporty", note: "Earlier sporty take before the styled set." },
  { src: heroRefined, name: "Hero — Refined", note: "Polished hero rendering used in marketing experiments." },
];

const REF_VARIANTS = [
  { src: refHero, name: "Ref — Hero (sign-in pose)", note: "Tall full-body pose holding a bracket. Drop-in replacement for the current hero on the login page." },
  { src: refLogo, name: "Ref — Logo mark", note: "Square mark for the header, favicon, and apple-touch icon." },
  { src: refOg, name: "Ref — Social/OG card (original)", note: "Dark gray 'Bracket' + neon green 'Goblin'. Lower contrast on the first word." },
  { src: refOgWhite, name: "Ref — Social/OG card (white 'Bracket')", note: "White 'Bracket' + neon green 'Goblin'. Higher contrast — easier to read in previews." },
];

type Option = { src: string; name: string; note: string };

function Grid({ items }: { items: Option[] }) {
  return (
    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
      {items.map((o) => (
        <div
          key={o.name}
          className="rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-card)]"
        >
          <div className="flex aspect-square items-center justify-center rounded-xl bg-background">
            <img
              src={o.src}
              alt={o.name}
              loading="lazy"
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold text-foreground">
            {o.name}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{o.note}</p>
        </div>
      ))}
    </div>
  );
}

function MascotOptionsPage() {
  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-5xl">
        <h1 className="font-display text-3xl font-bold text-foreground">Mascot Options</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Temporary preview page. Pick a direction (or ask for tweaks — recolor jersey, swap prop, change pose).
        </p>

        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold text-foreground">
            Ref variants (proposed swap)
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Three new renders of the Ref character for the actual spots the mascot lives: sign-in hero, header logo mark, and the social share card.
          </p>
          <Grid items={REF_VARIANTS} />
        </section>

        <section className="mt-12">
          <h2 className="font-display text-xl font-semibold text-foreground">
            Styled goblins
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Most recent set — same character in different outfits.
          </p>
          <Grid items={STYLED_OPTIONS} />
        </section>

        <section className="mt-12">
          <h2 className="font-display text-xl font-semibold text-foreground">
            Earlier concepts
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Original 4 concepts generated before the styled set. Useful as a baseline comparison for "less demonic" direction.
          </p>
          <Grid items={EARLIER_CONCEPTS} />
        </section>
      </div>
    </div>
  );
}
